import { faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, useTheme, IconButton } from "@mui/material";
import isEqual from "lodash/isEqual";
import React, { useEffect, useState, useCallback } from "react";

import { Props, State } from "./interfaces";
import SharedWithTable from "./SharedWithTable/SharedWithTable";
import { Container } from "./styles";
import { getItemsToSave } from "./utils";
import ConfirmationChangesDialog from "../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../../containers/LoadingOverlay/LoadingOverlay";
import {
  ShareWithLegalEntitySIDTO,
  ShareWithLegalEntitySIFilter,
} from "../../../../../models/share-with-legal-entity-si";
import { SharedWithLegalEntitySIApiService } from "../../../../../services/shared-with-legal-entity-si/shared-with-legal-entity-si-api.service";
import { toastifyResponse } from "../../../../../utils/toastify-response/toastify-response";

export const AddEditSharedWith = ({ legalEntitySI, handleNotifyParent, readOnly }: Props) => {
  const theme = useTheme();

  const [state, setState] = useState<State>({
    isDialogOpen: false,
    loading: false,
    error: "",
    items: [],
    modifiedItems: [],
    isCCDOpen: false,
  });

  const loadItems = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      const response = await new SharedWithLegalEntitySIApiService().loadItems({
        legalEntitySI: legalEntitySI.id,
      } as ShareWithLegalEntitySIFilter);

      if (!response.error) {
        setState((state) => ({
          ...state,
          items: response.items,
          modifiedItems: response.items,
        }));
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }
      toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
    } catch (error: any) {
      setState((state) => ({
        ...state,
        error: error?.message ?? error,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [legalEntitySI]);

  useEffect(() => {
    legalEntitySI?.id && state.isDialogOpen && loadItems();
  }, [legalEntitySI?.id, state.isDialogOpen]);

  const resetAndCloseDialog = useCallback(() => {
    if (!isEqual(state.items, state.modifiedItems)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  }, [state.items, state.modifiedItems]);

  const handleDiscardChanges = useCallback(() => {
    setState((state) => ({
      ...state,
      isDialogOpen: false,
      loading: false,
      error: "",
      items: [],
      modifiedItems: [],
      isCCDOpen: false,
    }));
  }, []);

  const handleUpdate = useCallback(
    (action: "add" | "edit" | "remove", item: ShareWithLegalEntitySIDTO) => {
      if (action === "add") {
        setState((state) => ({
          ...state,
          modifiedItems: [item, ...state.modifiedItems],
        }));
      }

      if (action === "edit") {
        const idx = state.modifiedItems.findIndex((el) => el?.id === item.id);

        if (idx > -1) {
          const oldOnes = [...state.modifiedItems];

          oldOnes.splice(idx, 1, item);

          setState((state) => ({
            ...state,
            modifiedItems: oldOnes,
          }));
        }
      }

      if (action === "remove") {
        const idx = state.modifiedItems.findIndex((el) => el?.id === item.id);

        if (idx > -1) {
          const oldOnes = [...state.modifiedItems];

          oldOnes[idx].isDeleted = true;

          setState((state) => ({
            ...state,
            modifiedItems: oldOnes,
          }));
        }
      }
    },
    [state.modifiedItems]
  );

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    const itemsToSave = getItemsToSave(state.items, state.modifiedItems).map((item) => {
      return {
        ...item,
        legalEntitySI: { id: legalEntitySI?.id },
        legalEntity: legalEntitySI?.legalEntity,
      } as ShareWithLegalEntitySIDTO;
    });

    try {
      const response = await new SharedWithLegalEntitySIApiService().saveItems(itemsToSave);

      if (!response.error) {
        handleDiscardChanges();

        handleNotifyParent();
      } else {
        setState((state) => ({
          ...state,
          loading: false,
          error: response.error,
        }));
      }

      toastifyResponse(response);
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
        error: error as string,
      }));
    }
  }, [state.modifiedItems, handleNotifyParent, handleDiscardChanges, legalEntitySI, state.items]);

  return (
    <>
      <IconButton
        onClick={() => {
          setState((state) => ({ ...state, isDialogOpen: true }));
        }}
      >
        <FontAwesomeIcon
          icon={faUsers}
          color={theme.palette.primary.main}
        />
      </IconButton>

      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={"Shared with"}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <Container>
              <SharedWithTable
                items={state.modifiedItems}
                handleUpdate={handleUpdate}
                defaultLegalEntity={legalEntitySI.legalEntity}
                legalEntitySI={legalEntitySI}
                readOnly={readOnly}
              />
            </Container>
          </DialogContent>
          <DialogActions
            handleSave={handleSave}
            disabledSaveButton={state.loading || readOnly}
            handleCancel={resetAndCloseDialog}
            error={state.error}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};
